import {
  CHANGED_MAP_IDS__TEXTURE_MAP,
  REVERSE_TILING_MAP,
  TEXTURE_MAP,
  TEXTURE_MAP__TEXTURE_NAME_KEY,
  TILING_MAP,
  TILING_TYPE,
} from '../../config/constant/unityConstants';
import { getMaterialTilingKey, unitySizeToCM } from '../unityHelper';
import { getMaterial, getMaterialTexture, getTexture } from '../idb';
import { getDefaultTextureImageUrl } from '../../components/admin/curator/stylesSection/PropertiesTab/useGetDefaultObjectMaps';
import { decimals } from '../jsHelper';

export const parseUnityObject = async (data) => {
  const {
    isTexture,
    isTextureChanged,
    isNormal,
    isNormalMapChanged,
    isRoughnessMap,
    isRoughnessMapChanged,
    isAOMap,
    isAOMapChanged,
    isMetallicMap,
    isMetallicMapChanged,
    isTransparency,
    isTransparencyMapChanged,
    isDisplacementMap,
    isDisplacementMapChanged
  } = data;

  const normalGeneratedFromDesign = isNormalGeneratedFromDesign(data);
  const getTilingOption = (tilingType) => {
    return data.tilingOptions.find((o) => o.tilingOptionType === tilingType);
  };

  const getMaterialId = (idKey) => {
    return parseInt(data.changedMapsPath[idKey]) || undefined;
  };

  const getMapImageById = async (idKey) => {
    const id = data.changedMapsPath[idKey];
    if (!id) return;

    // if (idKey === CHANGED_MAP_PATHS_MAP.normal && normalGeneratedFromDesign) {
    //   // TODO: work on this one
    //   return '';
    // }

    const texture = await getTexture(parseInt(id));
    return texture?.file;
  };

  const getTilingProperties = (tilingType) => {
    const key = TILING_MAP[tilingType];

    if (!key || !data.tilingOffsetValue[key]) return {};

    return convertTilingtypeData(data.tilingOffsetValue[key]);
  };

  const getUst = (tilingType) => {
    const key = TILING_MAP[tilingType];
    if (!key || !data.tilingOffsetValue[key]) return false;
    return !data.materialTilingKeys?.includes(key);
  };

  const materialTilingKey = getMaterialTilingKey(data);
  const materialTilingType = REVERSE_TILING_MAP[materialTilingKey];
  const materialTilingOption = getTilingOption(TILING_TYPE.MATERIAL);
  const textureTilingOption = getTilingOption(TILING_TYPE.TEXTURE);

  const getUrl = async (textureName) => {
    return await getDefaultTextureImageUrl({
      textureName,
      materialId: data.loadedMaterialId,
    });
  };

  const getMapImage = async (mapType, applied, changed) => {
    if (!applied)
      return {
        hasMap: false,
      };

    if (changed) {
      if (mapType === TEXTURE_MAP.DIFFUSE && data.isTexturefromMaterialPreset) {
        const materialTexture = await getMaterialTexture(data.materialTextureId);
        const imageUrl = materialTexture.texture_file_thumb;
        return {
          hasMap: Boolean(imageUrl),
          mapUrl: imageUrl,
        };
      }

      const idKey = CHANGED_MAP_IDS__TEXTURE_MAP[mapType];
      return {
        hasMap: true,
        mapUrl: await getMapImageById(idKey),
      };
    } else {
      const textureKey = TEXTURE_MAP__TEXTURE_NAME_KEY[mapType];
      const textureName = data[textureKey];
      const imageUrl = await getUrl(textureName);
      return {
        hasMap: Boolean(textureName) || Boolean(imageUrl),
        mapUrl: imageUrl,
      };
    }
  };

  const [
    diffuseMapImage,
    roughnessMapImage,
    normalMapImage,
    transparencyMapImage,
    aoMapImage,
    metallicMapImage,
    displacementMapImage
  ] = await Promise.all([
    getMapImage(TEXTURE_MAP.DIFFUSE, isTexture, isTextureChanged),
    getMapImage(TEXTURE_MAP.ROUGHNESS, isRoughnessMap, isRoughnessMapChanged),
    getMapImage(TEXTURE_MAP.NORMAL, isNormal, isNormalMapChanged),
    getMapImage(TEXTURE_MAP.TRANSPARENCY, isTransparency, isTransparencyMapChanged),
    getMapImage(TEXTURE_MAP.AMBIENTOCCLUSION, isAOMap, isAOMapChanged),
    getMapImage(TEXTURE_MAP.METALLIC, isMetallicMap, isMetallicMapChanged),
    getMapImage(TEXTURE_MAP.DISPLACEMENT, isDisplacementMap, isDisplacementMapChanged),
  ]);

  const getMaterialImage = async () => {
    // else if (data.isTexturefromMaterialPreset) {
    //   const materialTexture = await getMaterialTexture(data.materialTextureId)
    //   return  materialTexture.texture_file_thumb;
    // }

    const diffuseId = getMaterialId('diffuseId');
    if (data.loadedMaterialId && !isNaN(parseInt(data.loadedMaterialId))) {
      const material = await getMaterial(parseInt(data.loadedMaterialId));
      return material?.thumbnail;
    } else if (diffuseId) {
      const texture = await getTexture(parseInt(diffuseId));
      return texture?.file;
    }
  };

  const hasDefaultDiffuseTexture = data.isDiffuse && !data.isDiffuseMapChanged;
  const hasDefaultNormalTexture = data.isNormal && !data.isNormalMapChanged;
  const hasDefaultRoughnessTexture = data.isRoughnessMap && !data.isRoughnessMapChanged;
  const hasDefaultAOTexture = data.isAOMap && !data.isAOMapChanged;
  const hasDefaultMetallicTexture = data.isMetallicMap && !data.isMetallicMapChanged;
  const hasDefaultTransparencyTexture =
    data.isTransparency && !data.isTransparencyMapChanged && Boolean(data.transparencyMapName);
  const hasDefaultDisplacementTexture = data.isDisplacementMap && !data.isDisplacementMapChanged;

  return {
    isTexture: data.isTexture, // to check if  texture is applied or not on the object
    colorValue: data.colorValue,

    material: {
      hideUI: !materialTilingOption || !materialTilingType, // hide if material is missing
      mapImage: await getMaterialImage(),
      hasMapImage: Boolean(data.loadedMaterialId),
      tilingType: TILING_TYPE.MATERIAL,
      materialId: parseInt(data.loadedMaterialId),
      ...getTilingProperties(materialTilingType),
      lock: materialTilingOption?.isLocked || false,
    },
    texture: {
      hideUI: !textureTilingOption, // hide if material is missing
      mapImage: diffuseMapImage.mapUrl,
      hasMapImage: diffuseMapImage.hasMap,
      materialId: getMaterialId('diffuseId'),
      tilingType: TILING_TYPE.TEXTURE,
      ...getTilingProperties(TILING_TYPE.TEXTURE),
      lock: textureTilingOption?.isLocked || false,
    },

    // check materialTilingKeys -> to know if "Use separate tiliing is enabled"
    glossiness: {
      mapImage: roughnessMapImage.mapUrl,
      hasMapImage: roughnessMapImage.hasMap,
      materialId: getMaterialId('roughnessId'),
      tilingType: TILING_TYPE.GLOSSY,
      applicable: true,
      applied: isRoughnessMap,
      intensity: decimals(data.roughnessIntensity, 2),
      showSliderWhenExpanded: data.roughnessIntensity > 0,
      invertMap: data.isRoughnessInvert,
      customMap: data.isCustomRoughnessMap || hasDefaultRoughnessTexture, //  -> use custom map is enabled when it's true
      customMapApplicable: true,
      useSeparateTiling: getUst(TILING_TYPE.GLOSSY),
      lock: getTilingOption(TILING_TYPE.GLOSSY)?.isLocked || false,
      texture: null,
      // TODO: separateTiling????

      ...getTilingProperties(TILING_TYPE.GLOSSY),
    },

    normal: {
      mapImage: normalMapImage.mapUrl,
      hasMapImage: normalMapImage.hasMap,
      materialId: getMaterialId('normalId'),
      tilingType: TILING_TYPE.DEPTH,
      applicable: true, // data.isNormal && data.normalIntensity > 0,
      applied: isNormal,
      intensity: decimals(data.normalIntensity, 2),
      showSliderWhenExpanded: data.normalIntensity > 0,
      invertMap: data.isNormalInvert,
      customMap: data.isCustomNormalMap || hasDefaultNormalTexture,
      generateFromDesignVisible:
        (data.isTexture && data.isTextureChanged) || normalGeneratedFromDesign,
      // TODO: when user selects object -> get generateFromDesign image based on texture id
      generateFromDesign: normalGeneratedFromDesign,
      customMapApplicable: true,
      useSeparateTiling: getUst(TILING_TYPE.DEPTH),
      lock: getTilingOption(TILING_TYPE.DEPTH)?.isLocked || false,
      texture: null,
      // separateTiling:
      // TODO: separateTiling????
      // TODO; generate from design??? -> isNormal === true && isCustomNormalMap === false
      // can not be customMap && generate from design at the same time -> it's radio button

      // use seaparate tiling should be disabled until custom map or generate from design isenabled

      ...getTilingProperties(TILING_TYPE.DEPTH),
    },

    transparency: {
      mapImage: transparencyMapImage.mapUrl,
      hasMapImage: transparencyMapImage.hasMap,
      materialId: getMaterialId('transparencyId'),
      tilingType: TILING_TYPE.TRANSPARENCY,
      applicable: data.transparencyIntensity > 0,
      applied: isTransparency,
      intensity: decimals(data.transparencyIntensity, 2),
      showSliderWhenExpanded: data.transparencyIntensity > 0,
      invertMap: data.isTransparencyInvert,
      textureAlpha: data.isTransparencyFromAlbedo,
      customMap: data.isCustomTransparencyMap || hasDefaultTransparencyTexture,
      hasDefaultTexutre: hasDefaultTransparencyTexture,
      customMapApplicable: true,
      useSeparateTiling: getUst(TILING_TYPE.TRANSPARENCY),
      lock: getTilingOption(TILING_TYPE.TRANSPARENCY)?.isLocked || false,
      texture: null,
      ...getTilingProperties(TILING_TYPE.TRANSPARENCY),
    },

    ao: {
      mapImage: aoMapImage.mapUrl,
      hasMapImage: aoMapImage.hasMap,
      materialId: getMaterialId('AOId'),
      tilingType: TILING_TYPE.AO,
      applicable: data.isAOMap && data.aoIntensity > 0,
      applied: isAOMap,
      intensity: decimals(data.aoIntensity, 2),
      showSliderWhenExpanded: data.aoIntensity > 0,
      customMap: data.isCustomAOMap || hasDefaultAOTexture,
      invertMap: data.isAOInvert,
      customMapApplicable: true,
      useSeparateTiling: getUst(TILING_TYPE.AO),
      lock: getTilingOption(TILING_TYPE.AO)?.isLocked || false,
      ...getTilingProperties(TILING_TYPE.AO),
    },

    metallic: {
      mapImage: metallicMapImage.mapUrl,
      hasMapImage: metallicMapImage.hasMap,
      materialId: getMaterialId('metallicId'),
      tilingType: TILING_TYPE.METALLIC,
      applicable: data.metalIntensity > 0,
      applied: isMetallicMap,
      intensity: decimals(data.metalIntensity, 2),
      showSliderWhenExpanded: data.metalIntensity > 0,
      customMap: data.isCustomMetallicMap || hasDefaultMetallicTexture,
      invertMap: data.isMetallicInvert,
      customMapApplicable: true,
      useSeparateTiling: getUst(TILING_TYPE.METALLIC),
      lock: getTilingOption(TILING_TYPE.METALLIC)?.isLocked || false,

      ...getTilingProperties(TILING_TYPE.METALLIC),
    },

    displacement: {
      mapImage: displacementMapImage.mapUrl,
      hasMapImage: displacementMapImage.hasMap,
      materialId: getMaterialId('displacementId'),
      tilingType: TILING_TYPE.DISPLACEMENT,
      applicable: data.displacementIntensity > 0,
      applied: isDisplacementMap,
      intensity: decimals(data.displacementIntensity, 2),
      showSliderWhenExpanded: data.displacementIntensity > 0,
      customMap: data.isCustomDisplacementMap || hasDefaultDisplacementTexture,
      invertMap: data.isDisplacementInvert,
      customMapApplicable: true,
      useSeparateTiling: getUst(TILING_TYPE.DISPLACEMENT),
      lock: getTilingOption(TILING_TYPE.DISPLACEMENT)?.isLocked || false,

      ...getTilingProperties(TILING_TYPE.DISPLACEMENT),
    },

    emission: {
      tilingType: TILING_TYPE.EMISSION,
      intensity: decimals(data.emissionIntensity, 2),
      showSliderWhenExpanded: data.emissionIntensity > 0,
    },

    clearcoat: {
      tilingType: TILING_TYPE.CLEARCOAT,
      intensity: decimals(data.clearCoatValue, 2),
    },

    other: {
      tilingType: TILING_TYPE.OTHER,
      effectLightColorBounce: data.effectLightColorBounce,
      useSpecularCalculation: data.useSpecularCalculation,
    },
  };
};

export const convertTilingtypeData = (data, count = 2) => ({
  width: unitySizeToCM(data.xMatTiling, count),
  height: unitySizeToCM(data.yMatTiling, count),
  x: data.xMatOffset,
  y: data.yMatOffset,
  rotation: data.rotation,
});

export const isNormalGeneratedFromDesign = (data) => {
  return data.isNormalMapChanged && !data.isCustomNormalMap;
};
